import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Building2, LogOut, Loader2, MessageSquare } from 'lucide-react';
import { api } from './api';
import { useAuth } from './AuthContext';
import MyDealConversations from './MyDealConversations';

// /party — home for a title or escrow contact the loan officer added to a
// deal thread. They have no pipeline: just the deals they were seated on.
// One deal goes straight to its page.
export default function PartyPortalPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  // "Your portal" on the deal page sets `stay`, so one deal does not bounce them straight back.
  const stay = !!useLocation().state?.stay;
  const [deals, setDeals] = useState(null);
  const isParty = user?.role === 'party';

  useEffect(() => {
    if (!isParty) return undefined;
    let live = true;
    api.get('/deals/mine/external')
      .then(({ data }) => { if (live) setDeals(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setDeals([]); });
    return () => { live = false; };
  }, [isParty]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  if (!isParty) return <Navigate to="/portal" replace />;
  if (deals && deals.length === 1 && !stay) return <Navigate to={`/deal/${encodeURIComponent(deals[0].deal_id)}`} replace />;

  const doLogout = async () => { await logout(); navigate('/portal/login'); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="party-portal">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-brand-navy"><Building2 size={18} color="white" /></div>
          <div className="flex-1 font-serif text-lg font-bold text-brand-navy">Hi {user.name || 'there'}</div>
          <button type="button" onClick={doLogout} className="text-sm text-slate-600 underline inline-flex items-center gap-1"><LogOut size={13} /> Sign out</button>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
        {!deals && <Loader2 className="animate-spin text-slate-400" />}
        {deals && deals.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-10" data-testid="party-portal-empty">
            <MessageSquare size={28} className="mx-auto text-slate-300 mb-2" />
            You are not on any deal conversations right now.
          </div>
        )}
        {deals && (deals.length > 1 || (stay && deals.length === 1)) && <MyDealConversations />}
      </div>
    </div>
  );
}
